import { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import EntriService from '../services/EntriService';
import entriMessageHandler from '../services/EntriMessageHandler';
import { enableEntri } from '../data/api.js';

/**
 * Custom hook for managing Entri domain purchase flow
 * Opens the Entri modal for domain purchasing - results are communicated via postMessage
 *
 * @param {object} options - Hook configuration options
 * @param {string} options.subdomain - Ghost subdomain for DNS configuration
 * @param {string} options.siteId - Ghost site id
 * @param {function} [options.onError] - Callback when error occurs
 * @param {function} [options.onDomainPurchased] - Callback when domain is successfully purchased
 *
 * @returns {object} Hook state and methods
 * @returns {boolean} returns.isLoading - Whether purchase flow is initializing
 * @returns {Error|null} returns.error - Error object if something went wrong
 * @returns {function} returns.initiatePurchase - Function to start purchase flow
 *
 * @example
 * const {isLoading, error, initiatePurchase} = useEntriDomainPurchase({
 *   subdomain: 'my-site',
 *   onError: (error) => console.error('Failed to open purchase modal:', error),
 *   onDomainPurchased: (domain) => console.log('Purchased domain:', domain)
 * });
 */
const useEntriDomainPurchase = ({ subdomain, siteId, onError, onDomainPurchased }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const subscriptionIdRef = useRef(null);

    // Subscribe to Entri messages when purchase is initiated
    const initiatePurchase = useCallback(async () => {
        // Reset error state
        setError(null);
        setIsLoading(true);

        try {
            await enableEntri({ siteId });

            // Subscribe to Entri messages before opening modal
            // The handler will auto-unsubscribe after receiving a message
            subscriptionIdRef.current = entriMessageHandler.subscribe((result) => {
                if (result.success && result.domain) {
                    // Call the success callback with the purchased domain
                    onDomainPurchased?.(result.domain);
                }
                // Clear subscription reference since it auto-unsubscribes
                subscriptionIdRef.current = null;
            });

            // Create service instance with required parameters
            const entriService = new EntriService(subdomain);

            // Initialize purchase flow - opens modal, returns void
            // Results are communicated via postMessage
            await entriService.initializePurchaseFlow();
        } catch (err) {
            // If error occurs, unsubscribe from messages
            if (subscriptionIdRef.current) {
                entriMessageHandler.unsubscribe(subscriptionIdRef.current);
                subscriptionIdRef.current = null;
            }

            // Error is already logged by EntriService
            setError(err);
            onError?.(err);
        } finally {
            setIsLoading(false);
        }
    }, [subdomain, onError, onDomainPurchased, siteId]);

    // Clean up subscription on unmount
    useEffect(() => {
        return () => {
            if (subscriptionIdRef.current) {
                entriMessageHandler.unsubscribe(subscriptionIdRef.current);
                subscriptionIdRef.current = null;
            }
        };
    }, []);

    return {
        isLoading,
        error,
        initiatePurchase,
    };
};

useEntriDomainPurchase.propTypes = {
    subdomain: PropTypes.string.isRequired,
    onError: PropTypes.func,
    onDomainPurchased: PropTypes.func,
};

export default useEntriDomainPurchase;
